import { useState, useEffect } from 'react'
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonSpinner,
  IonText,
  IonCheckbox,
  IonLabel,
} from '@ionic/react'
import { publicApolloClient } from '../../lib/apolloPublic'
import { GET_LEGAL_CONTENT } from '../../graphql/queries/legalContent'
import type { GetLegalContentQuery } from '../../types/graphql-generated'

interface LegalContentModalProps {
  isOpen: boolean
  onClose: () => void
  mode: 'accept' | 'view'
  onAccept?: () => void
}

export const LegalContentModal = ({ isOpen, onClose, mode, onAccept }: LegalContentModalProps) => {
  const [htmlContent, setHtmlContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(isOpen)
  const [error, setError] = useState<string | null>(null)
  const [accepted, setAccepted] = useState(false)
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)

  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen)
    if (isOpen) {
      setLoading(true)
      setError(null)
      setAccepted(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return

    publicApolloClient
      .query<GetLegalContentQuery>({ query: GET_LEGAL_CONTENT, fetchPolicy: 'network-only' })
      .then((result) => {
        if (result.error) {
          setError('No se pudo cargar el contenido legal')
        } else {
          setHtmlContent(result.data?.legalContent ?? null)
        }
      })
      .catch(() => {
        setError('No se pudo cargar el contenido legal')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [isOpen])

  const handleAccept = () => {
    if (onAccept) {
      onAccept()
    }
  }

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Términos y Condiciones</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>Cerrar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}

        {!loading && !error && htmlContent && (
          <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}

        {mode === 'accept' && !loading && !error && (
          <div className="flex flex-col gap-4 mt-6">
            <div className="flex items-center gap-3">
              <IonCheckbox
                checked={accepted}
                onIonChange={(e) => setAccepted(e.detail.checked)}
                aria-label="Aceptar términos y condiciones"
              />
              <IonLabel className="text-sm">
                He leído y acepto los Términos y Condiciones y la Política de Privacidad
              </IonLabel>
            </div>
            <IonButton
              expand="block"
              disabled={!accepted}
              onClick={handleAccept}
            >
              Continuar
            </IonButton>
          </div>
        )}
      </IonContent>
    </IonModal>
  )
}
